import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import logo from "@/assets/images/logo.png";
import signup_left from "@/assets/images/signup_left.png";
import google from "@/assets/images/google.png";
import microsoft from "@/assets/images/microsoft.png";
import { useMainStore } from "@/stores";
import {
  useCheckEmail,
  useRegister,
  useLogin,
  useForgotPassword,
} from "@/hooks/useAuth";
import { Eye, EyeOff } from "lucide-react";
import axiosInstanceNode from "@/api/axiosInstanceNode";
import { handleAuthSuccess } from "@/shared/utils/helperFunctions";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import ForgotModal from "./ForgotModal";
import {
  COMPANY_EMAIL_REQUIRED_MESSAGE,
  isFreeEmailDomain,
} from "@/shared/utils/freeEmailProviders";

const Signup = () => {
  const { setUser } = useMainStore();
  const navigate = useNavigate();

  // React Query hooks
  const checkEmailMutation = useCheckEmail();
  const registerMutation = useRegister();
  const loginMutation = useLogin();
  const forgotPasswordMutation = useForgotPassword();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
  });
  const [emailExists, setEmailExists] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [error, setError] = useState("");
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Derived loading states from mutations
  const isCheckingEmail = checkEmailMutation.isPending;
  const isLoggingIn = loginMutation.isPending;
  const isSigningUp = registerMutation.isPending;
  const isResettingPassword = forgotPasswordMutation.isPending;

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  }, []);

  const handleEmailSubmit = async () => {
    if (!formData.email) {
      setError("Please enter your email address.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    try {
      const result = await checkEmailMutation.mutateAsync(formData.email);
      if (!result.exists && isFreeEmailDomain(formData.email)) {
        setError(COMPANY_EMAIL_REQUIRED_MESSAGE);
        return;
      }
      setEmailExists(result.exists);
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Couldn't verify email. You can continue signing up.",
      );
      setEmailExists(false);
    }
  };

  const handleSignup = async () => {
    if (
      !formData.email ||
      !formData.name ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError("Please fill in all fields.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (isFreeEmailDomain(formData.email)) {
      setError(COMPANY_EMAIL_REQUIRED_MESSAGE);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    try {
      const response = await registerMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
        name: formData.name,
      });

      await handleAuthSuccess(
        response.user,
        response.token,
        "Registration successful!",
        setUser,
        navigate,
        true, // isSignup = true
        response.onboardFlag ?? false,
        false,
        {
          code: response.code,
          hasAccessRequest: response.access_request_sent ?? false,
          role: response.user?.role,
          requestTarget: response.request_target,
          orgName: response.org_name,
          requestSubmittedAt: response.request_submitted_at,
        },
      );
    } catch (err) {
      setError(err.message || "An error occurred during signup.");
    }
  };

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      setError("Please enter your email and password.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      const response = await loginMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
      });

      await handleAuthSuccess(
        response.user,
        response.token,
        response.code === "access_rejected"
          ? "You can view your request status."
          : "Login successful!",
        setUser,
        navigate,
        false,
        response.onboardFlag ?? false,
        false,
        {
          code: response.code,
          hasAccessRequest: response.access_request_sent ?? false,
          role: response.user?.role,
          requestTarget: response.request_target,
          orgName: response.org_name,
          requestSubmittedAt: response.request_submitted_at,
          approval_status: response.approval_status,
          approvalStatus: response.approval_status,
          rejectedBy: response.rejected_by,
        },
      );
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    }
  };

  const handleForgotPassword = async () => {
    setShowForgotModal(true);
  };

  // Helper function to get input border class
  const getInputBorderClass = useCallback(
    (inputName) => {
      return focusedInput === inputName
        ? "border-secondary"
        : "border-gray-300 dark:border-gray-700";
    },
    [focusedInput],
  );

  // Input field props helper
  const getInputProps = useCallback(
    (name, placeholder, type = "text", onEnterKey) => {
      const isPasswordRelated = name === "password" || name === "confirmPassword";
      return {
        type,
        name,
        placeholder,
        value: formData[name],
        onChange: handleChange,
        onFocus: () => setFocusedInput(name),
        onBlur: () => setFocusedInput(null),
        onKeyDown: (e) => {
          if (e.key === "Enter" && onEnterKey) {
            e.preventDefault();
            onEnterKey();
          }
        },
        className: `${isPasswordRelated ? "w-full pr-10" : "w-5/6"} p-3 border-b-[1.5px] bg-transparent outline-none text-[13px] text-gray-800 dark:text-gray-100 mb-3.5 transition-colors font-normal font-inter placeholder:font-normal placeholder:text-gray-500 dark:placeholder:text-gray-400
         ${getInputBorderClass(name)}`,
      };
    },
    [formData, handleChange, getInputBorderClass],
  );

  const handleMicrosoftLogin = async () => {
    const endpoint = `${process.env.REACT_APP_API_URL || "<missing>"}/auth/microsoft`;
    console.info("[MicrosoftLoginDebug] starting request", {
      endpoint,
      origin: window.location.origin,
    });

    try {
      const response = await axiosInstanceNode.get("/auth/microsoft");
      console.info("[MicrosoftLoginDebug] request succeeded", {
        status: response.status,
        hasAuthUrl: Boolean(response.data?.url),
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("[MicrosoftLoginDebug] request failed", {
        message: error?.message,
        code: error?.code,
        baseURL: error?.config?.baseURL,
        url: error?.config?.url,
        status: error?.response?.status,
        responseData: error?.response?.data,
      });
      throw error;
    }
  };

  const loginwithGoogle = async () => {
    const response = await axiosInstanceNode.get("/auth/google");
    window.location.href = response.data.url;
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row w-full h-[calc(100vh-80px)] bg-white dark:bg-gray-950">
        {/* Left side */}
        <div className="hidden lg:flex lg:w-1/2 flex-col gap-4 items-center justify-center bg-gray-100 dark:bg-gray-900 rounded-none lg:rounded-2xl px-6 py-10">
          <img
            className="w-full max-w-md h-auto object-contain"
            src={signup_left}
            alt="Sales automation illustration"
          />

          <div className="text-center flex flex-col gap-1">
            <h2 className="text-secondary font-bold text-xl xl:text-2xl">
              Boost Sales with Smart Email Automation
            </h2>

            <p className="text-gray-600 dark:text-gray-400 text-sm xl:text-base font-normal">
              Personalized emails that drive results.
            </p>
          </div>
        </div>

        {/* Right side */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 md:px-10 py-8">
          <div className="w-full max-w-md">
            <img
              src={logo}
              alt="Salesworx.ai"
              className="h-14 sm:h-16 mb-3 mx-auto object-contain"
            />

            <h2 className="text-xl sm:text-2xl font-bold mb-2 text-center text-gray-800 dark:text-gray-100 leading-snug">
              Where sales works at lightning speed
            </h2>

            {emailExists && (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center mb-5">
                Already a user? Enter your password to get started
              </p>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-center text-sm">
                {error}
              </div>
            )}

            {emailExists === null ? (
              <>
                <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                  Sign up or log in for sales intelligence at your fingertips
                </p>

                {/* Google button */}
                <button
                  className="text-gray-700 dark:text-gray-200 font-bold text-sm w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 p-3 rounded-3xl mb-3 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={loginwithGoogle}
                >
                  <img
                    src={google}
                    alt="Google"
                    className="w-[18px] h-[18px] mr-2"
                  />
                  Continue with Google
                </button>

                {/* Microsoft button */}
                <button
                  className="text-gray-700 dark:text-gray-200 font-bold text-sm w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 p-3 rounded-3xl mb-4 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={handleMicrosoftLogin}
                >
                  <img
                    src={microsoft}
                    alt="Microsoft"
                    className="w-4 h-4 mr-2"
                  />
                  Continue with Microsoft
                </button>

                {/* Divider */}
                <div className="relative text-center mb-5 w-full">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
                  </div>

                  <div className="relative">
                    <span className="px-2 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-950">
                      OR
                    </span>
                  </div>
                </div>

                {/* Email input */}
                <div className="flex justify-center items-center">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter Your Work Email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedInput("email")}
                    onBlur={() => setFocusedInput(null)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !isCheckingEmail &&
                        formData.email
                      ) {
                        handleEmailSubmit();
                      }
                    }}
                    className={`w-full p-3 border-b-[1.5px] bg-transparent outline-none text-sm text-gray-800 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors mb-4 ${getInputBorderClass(
                      "email",
                    )}`}
                  />
                </div>

                <p className="text-xs mb-5 text-gray-400 text-center leading-relaxed">
                  By signing up, you agree to the{" "}
                  <a
                    href="https://salesworx.ai/terms-conditions/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-gray-400 hover:text-secondary transition-colors"
                  >
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://salesworx.ai/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-gray-400 hover:text-secondary transition-colors"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>

                {/* Continue button */}
                <div className="flex justify-center items-center">
                  <PrimaryButton
                    onClick={handleEmailSubmit}
                    disabled={isCheckingEmail || !formData.email}
                    isLoading={isCheckingEmail}
                    startContent={false}
                    className="w-full rounded-full px-3 py-5 text-sm font-normal"
                  >
                    {isCheckingEmail ? "Checking Email" : "Continue"}
                  </PrimaryButton>
                </div>
              </>
            ) : emailExists ? (
              <>
                <div className="flex flex-col gap-3">
                  <div className="flex justify-center items-center">
                    <div className="relative w-5/6 flex items-center justify-center">
                      <input
                        {...getInputProps(
                          "password",
                          "Enter Your Password",
                          showPassword ? "text" : "password",
                          () => {
                            if (!isLoggingIn && formData.password) {
                              handleLogin();
                            }
                          },
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-2 top-3 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <p
                    onClick={handleForgotPassword}
                    className={`text-xs text-right text-gray-600 transition-colors mb-6 ${
                      isResettingPassword
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:text-secondary"
                    }`}
                  >
                    {isResettingPassword
                      ? "Sending reset instructions..."
                      : "Forgot Password?"}
                  </p>
                </div>

                <div className="flex justify-center items-center">
                  <PrimaryButton
                    onClick={handleLogin}
                    disabled={isLoggingIn}
                    isLoading={isLoggingIn}
                    startContent={false}
                    className="w-full mb-4 rounded-full px-3 py-5 text-sm font-normal"
                  >
                    {isLoggingIn ? "Logging in" : "Log In"}
                  </PrimaryButton>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3">
                <input {...getInputProps("name", "Enter Your Full Name")} />

                <div className="relative w-5/6 flex items-center justify-center">
                  <input
                    {...getInputProps(
                      "password",
                      "Create Your Password",
                      showPassword ? "text" : "password",
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-2 top-3 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <div className="relative w-5/6 flex items-center justify-center">
                  <input
                    {...getInputProps(
                      "confirmPassword",
                      "Confirm Your Password",
                      showConfirmPassword ? "text" : "password",
                      () => {
                        if (
                          !isSigningUp &&
                          formData.name &&
                          formData.password &&
                          formData.confirmPassword
                        ) {
                          handleSignup();
                        }
                      },
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    className="absolute right-2 top-3 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <PrimaryButton
                  onClick={handleSignup}
                  disabled={isSigningUp}
                  isLoading={isSigningUp}
                  startContent={false}
                  className="w-full mb-4 rounded-full px-3 py-5 text-sm font-inter font-normal"
                >
                  {isSigningUp ? "Signing up" : "Sign Up"}
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>

        {/* forgot password modal */}
        {showForgotModal && (
          <ForgotModal
            showForgotModal={showForgotModal}
            setShowForgotModal={setShowForgotModal}
            forgotPasswordMutation={forgotPasswordMutation}
          />
        )}
      </div>
    </>
  );
};

export default Signup;
